import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Cpu, Globe, RefreshCw, Gauge, Zap } from 'lucide-react';

const API = window.__API_BASE + '/api';

function SemaphoreBar({ name, max, inUse, available, accentColor }) {
  const pct = max > 0 ? (inUse / max) * 100 : 0;
  const isHot = pct >= 75;
  const isWarm = pct >= 50 && pct < 75;

  return (
    <div data-testid={`sem-bar-${name}`} className="group">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium text-gray-700 capitalize">
          {name.replace(/_/g, ' ')}
        </span>
        <span className={`text-xs font-mono font-semibold ${
          isHot ? 'text-red-500' : isWarm ? 'text-amber-500' : 'text-emerald-500'
        }`}>
          {inUse}/{max}
        </span>
      </div>
      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ease-out ${
            isHot ? 'bg-red-500' : isWarm ? 'bg-amber-400' : accentColor || 'bg-emerald-400'
          }`}
          style={{ width: `${Math.max(pct, 0)}%` }}
        />
      </div>
    </div>
  );
}

export default function ConcurrencyMonitor() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      const res = await fetch(`${API}/concurrency-status`, { signal: controller.signal });
      clearTimeout(timeout);
      if (res.ok) {
        const json = await res.json();
        setData(json);
        setLastUpdated(new Date());
      }
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Failed to fetch concurrency status:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchStatus, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchStatus]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 flex items-center justify-center">
        <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center text-gray-400">
        Could not load concurrency data
      </div>
    );
  }

  const totalStageInUse = data.stage_limits.reduce((s, l) => s + l.in_use, 0);
  const totalServiceInUse = data.service_limits.reduce((s, l) => s + l.in_use, 0);
  const totalStageMax = data.stage_limits.reduce((s, l) => s + l.max, 0);
  const totalServiceMax = data.service_limits.reduce((s, l) => s + l.max, 0);

  return (
    <div data-testid="concurrency-monitor" className="space-y-6">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Activity className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Concurrency Monitor</h2>
            <p className="text-xs text-gray-400">
              Real-time pipeline &amp; API usage
              {lastUpdated && ` · Updated ${lastUpdated.toLocaleTimeString()}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="toggle-auto-refresh"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              autoRefresh
                ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                : 'bg-gray-50 text-gray-500 border border-gray-200'
            }`}
          >
            {autoRefresh ? 'Live' : 'Paused'}
          </button>
          <button
            data-testid="manual-refresh"
            onClick={fetchStatus}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-1">
            <Cpu className="w-5 h-5 text-blue-500" />
            <span className="text-sm font-semibold text-gray-700">Pipeline Stages</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{totalStageInUse}<span className="text-base font-normal text-gray-400">/{totalStageMax}</span></p>
          <p className="text-xs text-gray-400 mt-1">Active project stage slots</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-1">
            <Globe className="w-5 h-5 text-indigo-500" />
            <span className="text-sm font-semibold text-gray-700">API Services</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{totalServiceInUse}<span className="text-base font-normal text-gray-400">/{totalServiceMax}</span></p>
          <p className="text-xs text-gray-400 mt-1">Active API call slots</p>
        </div>
      </div>

      {/* Stage Limits */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center gap-2">
          <Gauge className="w-5 h-5 text-blue-500" />
          <h3 className="font-semibold text-gray-900">Pipeline Stage Limits</h3>
          <span className="text-xs text-gray-400 ml-auto">Max concurrent projects per stage</span>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
          {data.stage_limits.map((s) => (
            <SemaphoreBar
              key={s.name}
              name={s.name}
              max={s.max}
              inUse={s.in_use}
              available={s.available}
              accentColor="bg-blue-400"
            />
          ))}
        </div>
      </div>

      {/* Service Limits */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center gap-2">
          <Zap className="w-5 h-5 text-indigo-500" />
          <h3 className="font-semibold text-gray-900">API Service Limits</h3>
          <span className="text-xs text-gray-400 ml-auto">Max concurrent calls per service</span>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
          {data.service_limits.map((s) => (
            <SemaphoreBar
              key={s.name}
              name={s.name}
              max={s.max}
              inUse={s.in_use}
              available={s.available}
              accentColor="bg-indigo-400"
            />
          ))}
        </div>
      </div>
      {/* Activity Tracker — real-time task counts from all code paths */}
      {data.activity && Object.keys(data.activity).length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center gap-2">
            <Activity className="w-5 h-5 text-emerald-500" />
            <h3 className="font-semibold text-gray-900">Live Activity</h3>
            <span className="text-xs text-gray-400 ml-auto">Tasks running right now</span>
          </div>
          <div className="p-5 flex flex-wrap gap-3">
            {Object.entries(data.activity).map(([name, count]) => (
              <div key={name} className={`px-3 py-2 rounded-xl border text-sm font-medium ${
                count > 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-gray-50 border-gray-200 text-gray-400'
              }`}>
                <span className="capitalize">{name.replace(/_/g, ' ')}</span>
                <span className="ml-2 font-bold">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
